import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, MapPin, Plus, RefreshCw, Trash2 } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { type BoundaryShape, serviceArea } from "@/api/endpoints";
import type {
  GeoLocationHit,
  ServiceAreaBoundary,
  ServiceAreaCheck,
  ServiceAreaCode,
  ServiceAreaOverview,
} from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { PageHeader } from "@/components/PageHeader";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { ToggleGroup } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { useDebounced } from "@/hooks/useDebounced";
import { formatDateTime, formatNumber, humanize } from "@/lib/utils";

const QUERY_KEY = ["service-area"];

const MODE_TONE: Record<ServiceAreaOverview["mode"], BadgeTone> = {
  off: "default",
  monitor: "warning",
  enforce: "good",
};

const CODE_STATUS: Record<ServiceAreaCode["status"], { label: string; tone: BadgeTone }> = {
  ok: { label: "Outline loaded", tone: "good" },
  no_outline: { label: "No outline — admits nobody", tone: "critical" },
  unknown_code: { label: "Unknown code", tone: "critical" },
};

export default function ServiceArea() {
  const { can } = useAuth();
  const canWrite = can("config.write");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = React.useState<{ code?: string; name?: string } | null>(
    null,
  );

  const query = useQuery({ queryKey: QUERY_KEY, queryFn: serviceArea.overview });

  const remove = useMutation({
    mutationFn: (code: string) => serviceArea.remove(code),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      toast.success("Outline removed");
    },
    onError: (err) =>
      toast.error(
        "Could not remove the outline",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  const data = query.data;

  return (
    <>
      <PageHeader
        title="Service area"
        description="The map outlines the app is allowed to work inside. Which areas are switched on, and how strictly, is set in Configuration."
        actions={
          canWrite && (
            <Button onClick={() => setEditing({})}>
              <Plus />
              Add outline
            </Button>
          )
        }
      />

      {query.isLoading ? (
        <Card>
          <TableSkeleton cols={6} />
        </Card>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : data ? (
        <div className="space-y-4">
          <GuardStatus data={data} />

          <Card>
            <CardHeader>
              <CardTitle>Outlines</CardTitle>
              <CardDescription>
                An outline does nothing until its code is in the allowed areas. Load it
                first, then add the code — an allowed area without an outline refuses
                everyone in it.
              </CardDescription>
            </CardHeader>
            {data.boundaries.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Area</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead>Allowed</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.boundaries.map((b: ServiceAreaBoundary) => (
                    <TableRow key={b.code}>
                      <TableCell>
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {humanize(b.level)}
                          {b.parent_name ? ` · ${b.parent_name}` : ""}
                        </div>
                      </TableCell>
                      <TableCell className="tabular text-xs">{b.code}</TableCell>
                      <TableCell className="tabular">
                        {b.valid && b.vertices !== null ? (
                          formatNumber(b.vertices)
                        ) : (
                          <Badge variant="critical">Invalid</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-56 truncate text-xs" title={b.source ?? ""}>
                        {b.source ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">{formatDateTime(b.updated_at)}</TableCell>
                      <TableCell>
                        {b.in_use ? <Badge variant="good">Yes</Badge> : <Badge>No</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        {canWrite && (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditing({ code: b.code, name: b.name })}
                              aria-label={`Replace the outline of ${b.name}`}
                              title="Replace outline"
                            >
                              <RefreshCw className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={b.in_use || remove.isPending}
                              title={
                                b.in_use
                                  ? "Remove it from the allowed areas in Configuration first"
                                  : "Remove outline"
                              }
                              onClick={() => {
                                if (window.confirm(`Remove the outline of ${b.name}?`)) {
                                  remove.mutate(b.code);
                                }
                              }}
                              aria-label={`Remove the outline of ${b.name}`}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                icon={MapPin}
                title="No outlines loaded"
                description="Upload a GeoJSON boundary or draw a circle for each area you want to open."
                action={
                  canWrite && (
                    <Button onClick={() => setEditing({})}>
                      <Plus />
                      Add the first outline
                    </Button>
                  )
                }
              />
            )}
          </Card>

          <PointCheck />
        </div>
      ) : null}

      {editing && data && (
        <OutlineDialog
          initialCode={editing.code}
          initialName={editing.name}
          maxVertices={data.max_vertices}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function GuardStatus({ data }: { data: ServiceAreaOverview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Guard</CardTitle>
        <CardDescription>
          Change these under{" "}
          <Link to="/config" className="underline underline-offset-2">
            Configuration → Service area
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span>
            Mode <Badge variant={MODE_TONE[data.mode]}>{data.mode}</Badge>
          </span>
          <span className="text-muted-foreground">
            Boundary tolerance <span className="tabular text-foreground">{data.buffer_km} km</span>
          </span>
        </div>
        {data.codes.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {data.codes.map((c) => (
              <li
                key={c.code}
                className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm"
              >
                <span className="font-medium">{c.name ?? c.code}</span>
                <span className="tabular text-xs text-muted-foreground">{c.code}</span>
                <Badge variant={CODE_STATUS[c.status].tone}>{CODE_STATUS[c.status].label}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No allowed areas are set
            {data.mode === "enforce" ? " — under enforce the app refuses everyone." : "."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PointCheck() {
  const [lat, setLat] = React.useState("");
  const [lng, setLng] = React.useState("");
  const check = useMutation({
    mutationFn: () => serviceArea.check(Number(lat), Number(lng)),
  });
  const result: ServiceAreaCheck | undefined = check.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Test a location</CardTitle>
        <CardDescription>
          What the app's location check would answer here, and which outlines the point
          falls in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            check.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="check-lat">Latitude</Label>
            <Input
              id="check-lat"
              inputMode="decimal"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="30.9010"
              className="w-36"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="check-lng">Longitude</Label>
            <Input
              id="check-lng"
              inputMode="decimal"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="75.8573"
              className="w-36"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            disabled={check.isPending || !isCoordinate(lat, 90) || !isCoordinate(lng, 180)}
          >
            {check.isPending && <Spinner />}
            Check
          </Button>
        </form>

        {check.isError && (
          <FormError
            message={check.error instanceof ApiError ? check.error.message : "Check failed."}
          />
        )}
        {result && (
          <div className="mt-4 space-y-2 text-sm">
            <p>
              {result.allowed ? (
                <Badge variant="good">Allowed</Badge>
              ) : (
                <Badge variant="critical">Not allowed</Badge>
              )}{" "}
              {result.allowed
                ? `inside ${result.area_code}.`
                : result.mode === "enforce"
                  ? "The app would show “not available in your area”."
                  : `The guard is ${result.mode}, so the app would still work here.`}
            </p>
            {result.matches.length > 0 ? (
              <ul className="space-y-1 text-muted-foreground">
                {result.matches.map((m) => (
                  <li key={m.code}>
                    {m.inside ? "Inside" : "Within tolerance of"} {m.name}{" "}
                    <span className="tabular text-xs">({m.code})</span>
                    {!m.in_use && " — outline loaded, but not an allowed area"}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">Not inside any loaded outline.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function isCoordinate(value: string, limit: number): boolean {
  if (value.trim() === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= limit;
}

/** Positions in a GeoJSON document, so an oversized file is caught before upload. */
function countPositions(node: unknown): number {
  if (!Array.isArray(node)) {
    if (node && typeof node === "object") {
      return Object.values(node as Record<string, unknown>).reduce<number>(
        (sum, v) => sum + countPositions(v),
        0,
      );
    }
    return 0;
  }
  if (node.length >= 2 && typeof node[0] === "number" && typeof node[1] === "number") {
    return 1;
  }
  return node.reduce<number>((sum, v) => sum + countPositions(v), 0);
}

function OutlineDialog({
  initialCode,
  initialName,
  maxVertices,
  onClose,
}: {
  initialCode?: string;
  initialName?: string;
  maxVertices: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [target, setTarget] = React.useState<{ code: string; name: string } | null>(
    initialCode ? { code: initialCode, name: initialName ?? initialCode } : null,
  );
  const [search, setSearch] = React.useState("");
  const debounced = useDebounced(search.trim());
  const [shape, setShape] = React.useState<"geojson" | "circle">("geojson");
  const [file, setFile] = React.useState<{ name: string; doc: unknown; points: number } | null>(
    null,
  );
  const [lat, setLat] = React.useState("");
  const [lng, setLng] = React.useState("");
  const [radius, setRadius] = React.useState("25");
  const [source, setSource] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const hits = useQuery({
    queryKey: ["service-area-locations", debounced],
    queryFn: () => serviceArea.searchLocations(debounced),
    enabled: !target && debounced.length >= 2,
  });

  const save = useMutation({
    mutationFn: () => {
      const body: BoundaryShape =
        shape === "geojson"
          ? { geojson: file!.doc, source: source.trim() || file!.name }
          : {
              circle: {
                latitude: Number(lat),
                longitude: Number(lng),
                radius_km: Number(radius),
              },
              ...(source.trim() ? { source: source.trim() } : {}),
            };
      return serviceArea.upload(target!.code, body);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      const live = data.codes.some((c) => c.code === target!.code);
      toast.success(
        `Outline saved for ${target!.name}`,
        live
          ? "It is already an allowed area, so the change is live now."
          : `Add ${target!.code} to the allowed areas in Configuration to open it.`,
      );
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not save the outline."),
  });

  async function readFile(f: File) {
    setError(null);
    setFile(null);
    try {
      const doc: unknown = JSON.parse(await f.text());
      const points = countPositions(doc);
      if (points > maxVertices) {
        setError(
          `This file has ${formatNumber(points)} points; the limit is ${formatNumber(maxVertices)}. Simplify it first (e.g. mapshaper, 1–2%).`,
        );
        return;
      }
      setFile({ name: f.name, doc, points });
    } catch {
      setError("That file is not valid JSON. Upload a .geojson file.");
    }
  }

  const ready =
    target !== null &&
    (shape === "geojson"
      ? file !== null
      : isCoordinate(lat, 90) && isCoordinate(lng, 180) && Number(radius) > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialCode ? `Replace outline · ${initialName}` : "Add outline"}</DialogTitle>
          <DialogDescription>
            A state, a district or a single locality. Saving replaces any outline the area
            already has.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="area-search">Area</Label>
            {target ? (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{target.name}</span>{" "}
                  <span className="tabular text-xs text-muted-foreground">{target.code}</span>
                </span>
                {!initialCode && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setTarget(null)}>
                    Change
                  </Button>
                )}
              </div>
            ) : (
              <>
                <Input
                  id="area-search"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search a state, district or locality — e.g. Chennai"
                />
                {hits.isFetching && <Spinner className="size-4" />}
                {hits.data && hits.data.length > 0 && (
                  <ul className="max-h-56 overflow-y-auto rounded-md border border-border">
                    {hits.data.map((h: GeoLocationHit) => (
                      <li key={h.code}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => setTarget({ code: h.code, name: h.name })}
                        >
                          <span>
                            <span className="font-medium">{h.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {" "}
                              · {humanize(h.level)}
                              {h.parent_name ? `, ${h.parent_name}` : ""}
                            </span>
                          </span>
                          <span className="flex items-center gap-2">
                            {h.has_outline && <Badge variant="outline">Has outline</Badge>}
                            <span className="tabular text-xs text-muted-foreground">{h.code}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {hits.data && hits.data.length === 0 && (
                  <p className="text-xs text-muted-foreground">No area matches “{debounced}”.</p>
                )}
              </>
            )}
          </div>

          <ToggleGroup
            value={shape}
            onValueChange={(v) => {
              setShape(v as "geojson" | "circle");
              setError(null);
            }}
            options={[
              { label: "Upload GeoJSON", value: "geojson" },
              { label: "Draw a circle", value: "circle" },
            ]}
          />

          {shape === "geojson" ? (
            <div className="space-y-1.5">
              <Label htmlFor="geojson-file">Boundary file</Label>
              <label
                htmlFor="geojson-file"
                className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-border px-3 py-4 text-sm hover:bg-muted"
              >
                <FileUp className="size-5 text-muted-foreground" />
                {file ? (
                  <span>
                    {file.name}{" "}
                    <span className="text-xs text-muted-foreground">
                      · {formatNumber(file.points)} points
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    Choose a .geojson file — one Polygon or MultiPolygon, WGS84
                  </span>
                )}
              </label>
              <input
                id="geojson-file"
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void readFile(f);
                }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="circle-lat">Centre latitude</Label>
                <Input
                  id="circle-lat"
                  inputMode="decimal"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  placeholder="13.0827"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="circle-lng">Centre longitude</Label>
                <Input
                  id="circle-lng"
                  inputMode="decimal"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  placeholder="80.2707"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="circle-radius">Radius (km)</Label>
                <Input
                  id="circle-radius"
                  type="number"
                  min={0.1}
                  max={300}
                  step="any"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="outline-source">Source (optional)</Label>
            <Input
              id="outline-source"
              value={source}
              maxLength={200}
              onChange={(e) => setSource(e.target.value)}
              placeholder="Where this boundary came from, e.g. geoBoundaries"
            />
          </div>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || save.isPending}>
              {save.isPending && <Spinner />}
              Save outline
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
